import React from "react";
import {
  styled,
  TextField,
  TextFieldProps,
  ButtonProps,
  Button,
  SelectProps,
  Select,
  IconButtonProps,
  TooltipProps,
  Tooltip,
  DialogProps,
  Dialog,
} from "@mui/material";
import { TabList, TabPanel, TabPanelProps } from "@mui/lab";
import { TabListProps } from "@mui/lab/TabList";
import IconButton from "@mui/material/IconButton";
import hexToRgba from "hex-to-rgba";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { InternalNumberFormatBase } from "react-number-format/types/types";

interface CustomTabListProps extends TabListProps {
  noPadding?: boolean;
}

export function TabsStyled(props: TabListProps) {
  const TabsStyled = styled((innerProps: TabListProps) => <TabList {...innerProps} />)(
    ({ theme }) => ({
      backgroundColor: theme.palette.grey[500],
      display: "flex",
      alignItems: "center",
      padding: "0 4px",
      maxHeight: "40px",
      minHeight: "40px",
      "& .MuiTab-root": {
        flex: 1,
        "&.Mui-selected.AdronSecondaryTabs": {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.main,
        },
      },
      "& .MuiTabs-flexContainer": {},
    })
  );

  return <TabsStyled {...props} />;
}

const StyledTextField = styled(TextField)<TextFieldProps>(({ theme }) => ({
  "& .MuiInputLabel-root.Mui-focused": {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.grey[100] + "!important"
        : theme.palette.primary.main + "!important",
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.grey[100],
  },
}));

const StyledTextFieldEmpty = styled(TextField)<TextFieldProps>(({ theme }) => ({
  "& .MuiInputLabel-root.Mui-focused": {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.grey[100] + "!important"
        : theme.palette.primary.main + "!important",
  },
  "& .MuiInputBase-input": {
    paddingTop: 12,
    paddingBottom: 12,
    textAlign: "right",
    fontSize: "30px !important",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  "& .MuiInputBase-root::after": {
    border: 0,
  },
  "& .MuiFilledInput-root.Mui-focused::before": {
    border: 0,
  },
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.grey[500],
    borderRadius: 12,
  },
  "& .MuiInputBase-root.MuiFilledInput-root::before": {
    border: 0,
  },
  "& .MuiInputBase-root.MuiFilledInput-root:hover:not(.Mui-disabled, .Mui-error):before": {
    border: 0,
  },
}));

export function TextFieldStyled(props: TextFieldProps) {
  return <StyledTextField {...props} />;
}

export function TextFieldStyledEmpty(props: TextFieldProps) {
  return <StyledTextFieldEmpty {...props} />;
}

const StyledSelectField = styled(Select)<SelectProps>(({ theme }) => ({
  borderRadius: 12,
  "& .MuiInputBase-root.Mui-focused": {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.grey[100] + "!important"
        : theme.palette.primary.main + "!important",
  },
  "& .MuiInputBase-root": {
    color: theme.palette.grey[100],
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px !important",
    borderColor: hexToRgba(theme.palette.text.primary, 0.5),
  },
  // '& .MuiInputBase-root': {
  // 	color: theme.palette.grey[100],
  // },
}));

export function SelectStyled(props: SelectProps) {
  return <StyledSelectField {...props} />;
}

// export function TextFieldStyled(props: TextFieldProps) {
// 	const TextFieldStyled = styled((props: TextFieldProps) => (
// 		<TextField {...props} />
// 	))(({theme}) => ({
// 		'& .MuiInputLabel-root.Mui-focused': {
// 			color: theme.palette.mode === "dark" ? theme.palette.grey[100] + "!important" : theme.palette.primary.main + "!important",
// 		},
// 		'& .MuiInputLabel-root': {
// 			// #TODO: Разобраться с theme.palette.grey[100], почему прилетает какой-то левый цвет
// 			color: theme.palette.grey[100],
// 		}
// 	}));
//
// 	return <TextFieldStyled {...props} />;
// }

interface IconButtonPaperProps extends IconButtonProps {
  outlinedCustom?: boolean;
  hasBorder?: boolean;
}

export function IconButtonPaper({ outlinedCustom, hasBorder, ...props }: IconButtonPaperProps) {
  const IconButtonPaper = styled((props: IconButtonProps) => <IconButton {...props} />)(
    ({ theme }) => ({
      backgroundColor: outlinedCustom ? "transparent" : theme.palette.secondary.main + "!important",
      border: hasBorder ? `1px solid ${hexToRgba("#fff", 0.5)} !important` : "none",
      width: 40,
      height: 40,
      borderRadius: 12,
      "&.Mui-disabled": {
        opacity: 0.6,
      },
    })
  );

  return <IconButtonPaper {...props} />;
}

interface CustomTabPanelProps extends TabPanelProps {
  noPadding?: boolean;
}

const StyledTabPanel = styled(({ noPadding, ...props }: CustomTabPanelProps) => (
  <TabPanel {...props} />
))<CustomTabPanelProps>(({ noPadding }) => ({
  paddingTop: "16px",
  paddingBottom: noPadding ? "0 !important" : "16px !important",
  paddingLeft: noPadding ? "0 !important" : "16px !important",
  paddingRight: noPadding ? "0 !important" : "16px !important",
}));

export function TabPanelStyled(props: CustomTabPanelProps) {
  const { noPadding, ...otherProps } = props;
  return <StyledTabPanel noPadding={noPadding} {...otherProps} />;
}

export function ButtonStyled(props: ButtonProps) {
  const StyledButton = styled((props: ButtonProps) => <Button {...props} />)(({ theme }) => ({
    boxShadow: "none",
    borderRadius: 12,
    fontWeight: 600,
    border:
      props.variant === "outlined"
        ? `1px solid ${hexToRgba(theme.palette.text.primary, 0.5)} !important`
        : "none",
    color: theme.palette.text.primary,
    "&.MuiButton-containedSecondary:hover": {
      filter: "brightness(90%)",
      backgroundColor: theme.palette.secondary.main,
      boxShadow: "none",
    },
    "&.MuiButton-containedPrimary:hover": {
      filter: "brightness(90%)",
      boxShadow: "none",
    },
  }));

  return <StyledButton {...props} />;
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  decimalCustom?: number; // Новый пропс для кастомного количества знаков после запятой
}
const NumericFormatCustom = React.forwardRef<
  NumericFormatProps<InternalNumberFormatBase>,
  CustomProps
>(function NumericFormatCustom(props, ref) {
  const { onChange, decimalCustom = 2, ...other } = props; // Используем decimalCustom с значением по умолчанию

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        // if(values.formattedValue === '0,0') {
        // 	onChange({
        // 		target: {
        // 			name: props.name,
        // 			value: values.formattedValue,
        // 		},
        // 	});
        // } else {
        // 	onChange({
        // 		target: {
        // 			name: props.name,
        // 			value: values.value,
        // 		},
        // 	});
        // }
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      allowNegative={false}
      // allowEmptyFormatting={false}
      allowLeadingZeros={false}
      thousandSeparator={" "}
      decimalSeparator=","
      decimalScale={decimalCustom}
      // fixedDecimalScale
    />
  );
});

export { NumericFormatCustom };

const NumericFormatCustomV2 = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator={" "}
        decimalSeparator=","
        decimalScale={0}
        fixedDecimalScale
        valueIsNumericString
      />
    );
  }
);

export { NumericFormatCustomV2 };

export function TooltipStyled(props: TooltipProps) {
  const TooltipStyled = styled((props: TooltipProps) => (
    <Tooltip {...props}>{props.children}</Tooltip>
  ))(({ theme }) => ({
    "& .base-Popper-root": {
      "& .MuiTooltip-tooltip": {
        borderRadius: "8px",
        padding: "8px 12px",
        fontSize: "0.875rem",
        fontFamily: "Roboto",
        boxShadow: "0px -4px 6px -1px rgba(0, 0, 0, 0.10) !important;",
        color: theme.palette.text.primary,
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(21, 21, 21, 0.20) !important;"
            : "1px solid rgba(255, 255, 255, .2)",
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[500] : theme.palette.grey[500],
      },
    },
  }));

  return <TooltipStyled {...props}>{props.children}</TooltipStyled>;
}

export function DialogStyled(props: DialogProps) {
  const DialogStyled = styled((innerProps: DialogProps) => (
    <Dialog {...innerProps}>{innerProps.children}</Dialog>
  ))(({ theme }) => ({
    "& .MuiDialog-container": {
      "& .MuiDialog-paper": {
        backgroundColor: "transparent !important",
        marginLeft: "auto !important",
      },
      "& .MuiPaper-root": {
        backgroundColor: "none !important",
        borderRadius: "8px !important",
        border: `1px solid ${hexToRgba(theme.palette.text.primary, 0.2)} !important`,
      },
    },
  }));

  return <DialogStyled {...props}>{props.children}</DialogStyled>;
}
